/* eslint-disable no-unused-vars */
import Avatar from "@components/Avatar/Avatar"
import BottomBar from "@components/BottomBars/BottomBar"
import ModalSignOutConfirm from "@components/Modals/ModalSignOutConfirm"
import { Collapse, Drawer } from "@mantine/core"
import { useDisclosure, useScrollLock } from "@mantine/hooks"
import { auth } from "@utils/firebase"
import { useGetUser } from "@utils/hooks"
import type React from "react"
import { useMemo, useCallback, memo, useState } from "react"
import { useAuthState } from "react-firebase-hooks/auth"
import { Link, useNavigate } from "react-router-dom"
import { ReactComponent as ArrowRightIcon } from "../../assets/icons/arrow_right.svg"
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg"
import { ReactComponent as MenuIcon } from "../../assets/icons/menu.svg"
import { ReactComponent as PennyLogo } from "../../assets/icons/penny_logo.svg"
import { useSignOut } from "react-firebase-hooks/auth"

type NavbarProps = {}

const Navbar: React.FC<NavbarProps> = () => {
  const { data, isLoading } = useGetUser()
  const navigate = useNavigate()

  const [opened, { toggle }] = useDisclosure(false)
  const [_, setLockScroll] = useScrollLock(false)
  const [showSignOutModal, setShowSignOutModal] = useState(false)

  const showProfileLink = useMemo(() => {
    if (!isLoading) {
      const factFindSteps = [
        data?.form_fact_find_financial_details,
        data?.form_fact_find_personal_details,
        data?.form_fact_find_risk_reward,
      ]
        .map((i) => (i === true ? 1 : 0))
        .every((i) => i === 1)

      return factFindSteps
    }
  }, [isLoading, data])

  const [user] = useAuthState(auth)
  const [signOut] = useSignOut(auth)

  const onSignOut = useCallback(() => {
    setShowSignOutModal(true)
  }, [])

  const onCancelSignOut = useCallback(() => {
    setShowSignOutModal(false)
  }, [])

  const onToggleActiveCollapse = useCallback(() => {
    setLockScroll((prev) => !prev)
    toggle()
  }, [setLockScroll, toggle])

  const onToggleCloseCollapse = useCallback(() => {
    setLockScroll(false)
    toggle()
  }, [setLockScroll, toggle])

  const onConfirmSignOut = useCallback(async () => {
    onToggleCloseCollapse()
    setShowSignOutModal(false)
    await signOut()
  }, [signOut, onToggleCloseCollapse])

  const handleProfilePicRoute = useCallback(() => {
    if (opened) {
      onToggleCloseCollapse()
    }

    navigate("/my-profile")
  }, [opened, onToggleCloseCollapse, navigate])

  const handleLoginNavbar = useCallback(() => {
    if (opened) {
      onToggleCloseCollapse()
    }
  }, [opened, onToggleCloseCollapse])

  return (
    <>
      <nav
        className={
          "bg-pn_paper-1 sticky top-0 z-[99] left-0 right-0 border-b border-[#F3EFEA] pt-2 pb-1 pl-[19px] pr-4 md:pt-[19px] md:pb-[19px] lg:pl-[80px] lg:pr-[80px]"
        }
      >
        <div className="max-w-[1440px] w-full flex items-center justify-between mx-auto">
          <Link to={"/"}>
            <PennyLogo width={102} height={41} />
          </Link>

          <div className="flex items-center gap-x-2">
            {user?.uid ? (
              <Avatar onEvent={handleProfilePicRoute} size="small" user={user} />
            ) : (
              <Link to="/login" onClick={handleLoginNavbar} className="btn-secondary-sm">
                Login
              </Link>
            )}

            <button onClick={onToggleActiveCollapse} className="btn-secondary-icon-sm">
              {opened ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>

          <Collapse
            in={opened}
            className="absolute block md:hidden visible md:invisible left-0 right-0 border-t border-[#f3efea] top-full w-full"
          >
            <div className="bg-[#F4f4ed] h-full min-h-[calc(100dvh-59px)] flex flex-col">
              <div className="flex overflow-y-scroll h-[calc(100dvh-249px)] grow-[1] flex-col gap-y-2">
                <Link onClick={onToggleCloseCollapse} to="https://www.thisispenny.com/company#mission" className="flex justify-between items-center p-4">
                  <div className="flex flex-col gap-y-[3px]">
                    <span className="font-eightiescomeback text-xl font-normal">Our Mission</span>
                    <span className="text-pn-callout-regular">What makes us move, and why now</span>
                  </div>
                  <ArrowRightIcon />
                </Link>

                <div className="divider-dotted-transparent"></div>

                <Link onClick={onToggleCloseCollapse} to="#" className="flex justify-between items-center p-4">
                  <div className="flex flex-col gap-y-[3px]">
                    <span className="font-eightiescomeback text-xl font-normal">Our Service</span>
                    <span className="text-pn-callout-regular">Learn more about how Penny works</span>
                  </div>
                  <ArrowRightIcon />
                </Link>

                <div className="divider-dotted-transparent"></div>

                <Link onClick={onToggleCloseCollapse} to="#" className="flex justify-between items-center p-4">
                  <div className="flex flex-col gap-y-[3px]">
                    <span className="font-eightiescomeback text-xl font-normal">Ask Penny</span>
                    <span className="text-pn-callout-regular">Talk with a Penny advisor</span>
                  </div>
                  <ArrowRightIcon />
                </Link>

                {user?.uid && (
                  <>
                    <div className="divider-dotted-transparent"></div>

                    <Link
                      onClick={onToggleCloseCollapse}
                      to="/penny-space"
                      className="flex justify-between items-center p-4"
                    >
                      <div className="flex flex-col gap-y-[3px]">
                        <span className="font-eightiescomeback text-xl font-normal">Penny Space</span>
                        <span className="text-pn-callout-regular">Control everything about your pension</span>
                      </div>
                      <ArrowRightIcon />
                    </Link>

                    <div className="divider-dotted-transparent"></div>

                    {showProfileLink && (
                      <Link
                        onClick={onToggleCloseCollapse}
                        to="/my-profile"
                        className="flex justify-between items-center p-4"
                      >
                        <div className="flex flex-col gap-y-[3px]">
                          <span className="font-eightiescomeback text-xl font-normal">Your Profile</span>
                          <span className="text-pn-callout-regular">Update and manage your profile</span>
                        </div>
                        <ArrowRightIcon />
                      </Link>
                    )}
                  </>
                )}

                <div className="flex flex-col justify-center items-center w-full">
                  <BottomBar
                    layout={user?.uid ? "stacked" : "stacked_back"}
                    labelBack={user?.uid ? "Contact Us" : "Login"}
                    labelNext={user?.uid ? "Sign Out" : "Get started"}
                    onNext={user?.uid ? onSignOut : "/login"}
                    onBack={user?.uid ? "https://www.thisispenny.com/contact" : "/login"}
                    bordered={false}
                    variantsPrimary={user?.uid ? "alert" : "primary"}
                    onBackParalelEventLink={!user?.uid ? onToggleCloseCollapse : undefined}
                    onNextParalelEventLink={!user?.uid ? onToggleCloseCollapse : undefined}
                  />
                  <div className="text-pn-callout-regular text-center pb-4">
                    <a href="https://www.thisispenny.com/privacy-policy" className="hover:underline">Privacy Policy</a> • <a href="https://www.thisispenny.com/terms-of-business" className="hover:underline">Terms of Business</a>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>

          <Drawer
            className="hidden md:block"
            position="right"
            size={375}
            overlayProps={{
              opacity: 0.25,
            }}
            styles={{
              content: {
                background: "#F4f4ed",
                borderTopLeftRadius: "24px !important",
                borderBottomLeftRadius: "24px !important",
              },
              root: {
                background: "#F4f4ed",
              },
              body: {
                padding: "0px",
                height: "100%",
              },
            }}
            opened={opened}
            onClose={() => { }}
            closeOnClickOutside={false}
            withCloseButton={false}
          >
            <div className="bg-[#F4f4ed] h-full flex flex-col">
              <div className="py-4 px-6 flex items-center gap-x-6">
                <span className="text-pn-large-title text-pn_gray-950 flex-1">Menu</span>
                <div className="flex items-center gap-x-4">
                  <Avatar onEvent={handleProfilePicRoute} size="small" user={user!} />

                  <button onClick={onToggleActiveCollapse} className="btn-secondary-icon-sm">
                    <CloseIcon />
                  </button>
                </div>
              </div>

              <div className="flex overflow-y-scroll grow-[1] flex-col gap-y-2">
                <Link onClick={onToggleCloseCollapse} to="https://www.thisispenny.com/company#mission" className="flex justify-between items-center p-4">
                  <div className="flex flex-col gap-y-[3px]">
                    <span className="font-eightiescomeback text-xl font-normal">Our Mission</span>
                    <span className="text-pn-callout-regular">What makes us move, and why now</span>
                  </div>
                  <ArrowRightIcon />
                </Link>

                {/* <div className="divider-dotted-transparent"></div> */}

                {/* <Link onClick={onToggleCloseCollapse} to="#" className="flex justify-between items-center p-4">
                  <div className="flex flex-col gap-y-[3px]">
                    <span className="font-eightiescomeback text-xl font-normal">Our Service</span>
                    <span className="text-pn-callout-regular">Learn more about how Penny works</span>
                  </div>
                  <ArrowRightIcon />
                </Link> */}

                {/* <div className="divider-dotted-transparent"></div>

                <Link onClick={onToggleCloseCollapse} to="#" className="flex justify-between items-center p-4">
                  <div className="flex flex-col gap-y-[3px]">
                    <span className="font-eightiescomeback text-xl font-normal">Ask Penny</span>
                    <span className="text-pn-callout-regular">Talk with a Penny advisor</span>
                  </div>
                  <ArrowRightIcon />
                </Link> */}

                {user?.uid && (
                  <>
                    <div className="divider-dotted-transparent"></div>

                    <Link
                      onClick={onToggleCloseCollapse}
                      to="/penny-space"
                      className="flex justify-between items-center p-4"
                    >
                      <div className="flex flex-col gap-y-[3px]">
                        <span className="font-eightiescomeback text-xl font-normal">Penny Space</span>
                        <span className="text-pn-callout-regular">Control everything about your pension</span>
                      </div>
                      <ArrowRightIcon />
                    </Link>

                    <div className="divider-dotted-transparent"></div>

                    {showProfileLink && (
                      <Link
                        onClick={onToggleCloseCollapse}
                        to="/my-profile"
                        className="flex justify-between items-center p-4"
                      >
                        <div className="flex flex-col gap-y-[3px]">
                          <span className="font-eightiescomeback text-xl font-normal">Your Profile</span>
                          <span className="text-pn-callout-regular">Update and manage your profile</span>
                        </div>
                        <ArrowRightIcon />
                      </Link>
                    )}
                  </>
                )}

                <div className="flex mt-auto flex-col justify-center items-center w-full">
                  <BottomBar
                    layout={user?.uid ? "stacked" : "stacked_back"}
                    labelBack={user?.uid ? "Contact Us" : "Login"}
                    labelNext={user?.uid ? "Sign Out" : "Get started"}
                    onNext={user?.uid ? onSignOut : "/login"}
                    onBack={user?.uid ? "https://www.thisispenny.com/contact" : "/login"}
                    bordered={false}
                    variantsPrimary={user?.uid ? "alert" : "primary"}
                    onBackParalelEventLink={!user?.uid ? onToggleCloseCollapse : undefined}
                    onNextParalelEventLink={!user?.uid ? onToggleCloseCollapse : undefined}
                  />
                  <div className="text-pn-callout-regular text-center pb-4">
                    <a href="https://www.thisispenny.com/privacy-policy" className="hover:underline">Privacy Policy</a> • <a href="https://www.thisispenny.com/terms-of-business" className="hover:underline">Terms of Business</a>
                  </div>
                </div>
              </div>
            </div>
          </Drawer>
        </div>
      </nav>
      
      <ModalSignOutConfirm
        open={showSignOutModal}
        onClose={onCancelSignOut}
        onConfirm={onConfirmSignOut}
      />
    </>
  )
}

export default memo(Navbar)
